"use client";

import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface SwatchInteractionContextType {
  selectedSwatchOptionToken: string | undefined;
  setSelectedSwatchOptionToken: (token: string | undefined) => void;
  getActiveImage: (
    primaryImage: IImage,
    optionDetails?: ILeanProductTile.IOptionsDetails
  ) => IImage;
}

const SwatchInteractionContext = createContext<
  SwatchInteractionContextType | undefined
>(undefined);

interface SwatchInteractionProviderProps {
  children: ReactNode;
  firstOptionToken?: string;
}

export const SwatchInteractionProvider: React.FC<
  SwatchInteractionProviderProps
> = ({ firstOptionToken, children }) => {
  const [selectedSwatchOptionToken, setSelectedSwatchOptionToken] = useState<
    string | undefined
  >(firstOptionToken);

  const getActiveImage = useCallback(
    (
      primaryImage: IImage,
      optionDetails?: ILeanProductTile.IOptionsDetails
    ) => {
      if (!optionDetails?.swatch_details?.swatches_and_options) {
        return primaryImage;
      }

      if (!selectedSwatchOptionToken) {
        return primaryImage;
      }

      const activeSwatch =
        optionDetails.swatch_details.swatches_and_options.find(
          (swatch) => swatch.option_token === selectedSwatchOptionToken
        );

      return activeSwatch?.option_image || primaryImage;
    },
    [selectedSwatchOptionToken]
  );

  const value = {
    selectedSwatchOptionToken,
    setSelectedSwatchOptionToken,
    getActiveImage,
  };

  return (
    <SwatchInteractionContext.Provider value={value}>
      {children}
    </SwatchInteractionContext.Provider>
  );
};

export const useSwatchInteraction = () => {
  const context = useContext(SwatchInteractionContext);
  if (context === undefined) {
    throw new Error(
      "useSwatchInteraction must be used within a SwatchInteractionProvider"
    );
  }
  return context;
};
